import {useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import { IDeleteNotePayload, IPanelData, IUpdatePanel, IhightLightInfo, IhightLightObj } from '@/utils/ctype';
import {CloseSvg} from '../../Svg/index';
import style from './index.module.less';

interface Props {
  showNotePanel: boolean;
  highLightInfo: IhightLightInfo;
  noteHandle: () => void;
  updateNoteHandle: (payload: IUpdatePanel) => void;
  deleteNoteHandle: (payload: IDeleteNotePayload) => void;
}

interface NoteEntry {
  key: string;
  uid: string;
  pageDataIndex: number;
  part: number;
  title: string;
  content: string;
  timestamp: number;
}

export default function NotePanel(props: Props) {
  const {showNotePanel, noteHandle, highLightInfo, updateNoteHandle, deleteNoteHandle} = props;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [deleteConfirmations, setDeleteConfirmations] = useState<Record<string, boolean>>({});

  const notes = useMemo<NoteEntry[]>(() => {
    const result: NoteEntry[] = [];

    Object.entries(highLightInfo).forEach(([index, highlights]) => {
      const pageDataIndex = Number(index);
      if (!Number.isInteger(pageDataIndex)) {
        return;
      }

      Object.entries(highlights as IhightLightObj).forEach(([uid, item]) => {
        if (!item?.panelData) {
          return;
        }
        const panelData = item.panelData as IPanelData;
        result.push({
          key: `${pageDataIndex}:${uid}`,
          uid,
          pageDataIndex,
          part: pageDataIndex + 1,
          title: panelData.title || '',
          content: panelData.content || '',
          timestamp: panelData.timestamp || item.createdAt || 0,
        });
      });
    });

    return result.sort((left, right) => (
      left.pageDataIndex - right.pageDataIndex || left.timestamp - right.timestamp || left.uid.localeCompare(right.uid)
    ));
  }, [highLightInfo]);

  useEffect(() => {
    const noteKeys = new Set(notes.map((note) => note.key));
    setDrafts((previous) => {
      let changed = false;
      const next = Object.entries(previous).reduce<Record<string, string>>((all, [key, value]) => {
        if (noteKeys.has(key)) {
          all[key] = value;
        } else {
          changed = true;
        }
        return all;
      }, {});

      notes.forEach((note) => {
        if (!(note.key in next)) {
          next[note.key] = note.content;
          changed = true;
        }
      });

      return changed ? next : previous;
    });
    setDeleteConfirmations((previous) => {
      const next = Object.keys(previous).reduce<Record<string, boolean>>((all, key) => {
        if (noteKeys.has(key)) {
          all[key] = previous[key];
        }
        return all;
      }, {});
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
  }, [notes]);

  const saveNote = (note: NoteEntry) => {
    updateNoteHandle({
      pageDataIndex: note.pageDataIndex,
      uid: note.uid,
      value: {content: drafts[note.key] ?? note.content},
    });
  };

  const toggleDeleteConfirmation = (key: string) => {
    setDeleteConfirmations((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const deleteNote = (note: NoteEntry) => {
    deleteNoteHandle({pageDataIndex: note.pageDataIndex, uid: note.uid});
  };

  return (
    <aside className={classNames(style.notePanel, !showNotePanel && style.hidden)} aria-label="Notes">
      <div className={style.noteTop}>
        <span>Notes</span>
        <button className={style.noteClose} type="button" aria-label="Close notes" onClick={noteHandle}>{CloseSvg}</button>
      </div>
      <div className={style.noteList}>
        {notes.map((note) => (
          <article className={style.noteItem} key={note.key}>
            <div className={style.notePart}>Part {note.part}</div>
            <div className={style.noteTitle}>{note.title}</div>
            <textarea
              className={style.noteEditor}
              aria-label={`Note for Part ${note.part}`}
              value={drafts[note.key] ?? note.content}
              onChange={(event) => setDrafts((previous) => ({...previous, [note.key]: event.target.value}))}
            />
            <div className={style.noteActions}>
              <button className={style.saveButton} type="button" onClick={() => saveNote(note)}>save</button>
              <div className={style.deleteActions}>
                {deleteConfirmations[note.key] ? (
                  <button className={style.deleteButton} type="button" aria-label="Confirm deletion" onClick={() => deleteNote(note)}>Confirm delete</button>
                ) : null}
                <button className={style.deleteButton} type="button" onClick={() => toggleDeleteConfirmation(note.key)}>Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </aside>
  );
}
